if (!customElements.get('ml10-countdown')) {
  customElements.define('ml10-countdown', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const hours = Math.min(48, Math.max(1, Number(this.dataset.hours) || 12));
      this.cells = ['hours', 'minutes', 'seconds'].map(unit => this.querySelector('[data-unit="' + unit + '"]'));
      this.tick = () => {
        const remaining = Math.max(0, Math.ceil((this.deadline - Date.now()) / 1000));
        const values = [Math.floor(remaining / 3600), Math.floor(remaining / 60) % 60, remaining % 60];
        this.cells.forEach((cell, index) => { if (cell) cell.textContent = String(values[index]).padStart(2, '0'); });
        if (!remaining) clearInterval(this.timer);
      };
      this.start = () => {
        clearInterval(this.timer);
        this.deadline = Date.now() + hours * 3600000;
        this.tick();
        this.timer = setInterval(this.tick, 1000);
      };
      this.start();
      window.addEventListener('pageshow', event => { if (event.persisted) this.start(); }, {signal:this.controller.signal});
      document.addEventListener('visibilitychange', this.tick, {signal:this.controller.signal});
    }
    disconnectedCallback() { clearInterval(this.timer); this.controller?.abort(); }
  });
}
if (!customElements.get('ml10-progress')) {
  customElements.define('ml10-progress', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.update = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const progress = max > 0 ? Math.max(0, Math.min(100, window.scrollY / max * 100)) : 0;
        const fill = this.querySelector('.ml10-progress-fill');
        if (fill) fill.style.width = progress + '%';
        this.setAttribute('aria-valuenow', Math.round(progress));
      };
      let queued = false;
      const schedule = () => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; if (this.isConnected) this.update(); }); };
      window.addEventListener('scroll', schedule, {passive:true, signal});
      window.addEventListener('resize', schedule, {signal});
      this.observer = new ResizeObserver(schedule);
      this.observer.observe(document.body);
      this.update();
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  });
}
document.addEventListener('shopify:section:select', event => {
  if (event.target.matches('.ml10-section')) event.target.scrollIntoView({behavior:'instant',block:'center'});
});
if (!customElements.get('ml10-video')) {
  const players = new Set();
  let backgroundReady = false;
  let leadFallback;
  const startBackground = () => {
    if (backgroundReady) return;
    backgroundReady = true;
    clearTimeout(leadFallback);
    players.forEach(player => player.loadVideo());
  };
  customElements.define('ml10-video', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.loaded = Boolean(this.video.querySelector('source[src]'));
      this.visible = false;
      this.playPending = false;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.loop = true;
      this.video.controls = false;
      this.video.playsInline = true;
      this.video.setAttribute('loading', 'eager');
      this.loadVideo = () => {
        if (this.loaded || this.motion.matches) return;
        this.loaded = true;
        this.video.querySelectorAll('source[data-src]').forEach(source => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        this.video.preload = 'auto';
        this.video.load();
      };
      this.syncPlayback = () => {
        if (!this.visible || document.hidden || this.motion.matches) {
          this.video.pause();
          return;
        }
        this.loadVideo();
        if (this.video.readyState < 2 || !this.video.paused || this.playPending) return;
        this.playPending = true;
        this.video.play().catch(() => {}).finally(() => { this.playPending = false; });
      };
      this.video.addEventListener('loadeddata', this.syncPlayback, {signal});
      this.video.addEventListener('canplay', this.syncPlayback, {signal});
      document.addEventListener('visibilitychange', this.syncPlayback, {signal});
      window.addEventListener('pageshow', this.syncPlayback, {signal});
      document.addEventListener('pointerdown', this.syncPlayback, {passive: true, signal});
      this.motion.addEventListener('change', () => {
        if (backgroundReady || this.nearby || this.isLead) this.loadVideo();
        this.syncPlayback();
      }, {signal});
      this.isLead = players.size === 0;
      players.add(this);
      if (this.isLead) {
        this.video.addEventListener('loadeddata', startBackground, {once: true, signal});
        this.video.addEventListener('error', startBackground, {once: true, signal});
        this.loadVideo();
        if (this.video.readyState >= 2) startBackground();
        else leadFallback = setTimeout(startBackground, 6000);
      } else if (backgroundReady) {
        this.loadVideo();
      }
      if ('IntersectionObserver' in window) {
        this.loadObserver = new IntersectionObserver(entries => {
          const nearbyEntry = entries.find(entry => entry.isIntersecting);
          if (nearbyEntry) {
            this.nearby = true;
            if (backgroundReady || this.isLead || nearbyEntry.boundingClientRect.top < window.innerHeight) {
              this.loadVideo();
              this.loadObserver.disconnect();
            }
          }
        }, {rootMargin: '1400px 0px'});
        this.playObserver = new IntersectionObserver(entries => {
          this.visible = entries.some(entry => entry.isIntersecting);
          this.syncPlayback();
        }, {threshold: 0});
        this.loadObserver.observe(this);
        this.playObserver.observe(this);
      } else {
        this.visible = true;
        this.syncPlayback();
      }
    }
    disconnectedCallback() {
      this.video?.pause();
      this.loadObserver?.disconnect();
      this.playObserver?.disconnect();
      this.controller?.abort();
      players.delete(this);
      if (this.isLead && !backgroundReady) startBackground();
    }
  });
}
